import {
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { RequestLog, Trace, Transaction } from '@engine/models'

import { ErrorState } from '#components/ErrorState'
import { ExpandableJsonPanel } from '#components/ExpandableJsonPanel'
import { LoadingState } from '#components/LoadingState'
import { TraceRow } from '#components/TraceRow'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { formatDateTime, formatDuration, getDurationColor } from '#utils/format'
import { formatTimeDifference } from '#utils/timeDifference'

import type { TransactionWithCategory } from '../types'

interface ActivityTimelineProps {
  externalId?: string
  requestLogType?: string
  emptyMessage?: string
}

type TimelineItem =
  | {
      type: 'trace'
      data: TransactionWithCategory
      timestamp: string
      id: string
    }
  | { type: 'requestLog'; data: RequestLog; timestamp: string; id: string }
  | {
      type: 'tenantCallback'
      data: {
        callback: RequestLog['tenantCallback'][0]
        requestLog: RequestLog
      }
      timestamp: string
      id: string
    }

const RequestLogComponent = ({ requestLog }: { requestLog: RequestLog }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const tagColor = 'bg-purple-500/20 text-purple-300'

  return (
    <div className="rounded-box bg-neutral border border-base-300">
      <div
        className="p-4 cursor-pointer hover:bg-base-300/20 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div
              className={`px-2 py-1 rounded text-xs font-semibold ${tagColor} flex items-center gap-1.5`}
            >
              <DocumentTextIcon className="w-3 h-3" />
              REQUEST LOG
            </div>
            <span className="font-semibold">
              {requestLog.type?.toUpperCase()}
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-base-content/70">
              <span className="font-medium">Carrier:</span>{' '}
              <span className="font-mono">
                {requestLog.carrierCode?.join(', ') || 'N/A'}
              </span>
            </div>
            <div className="text-base-content/70">
              <span className="font-medium">Time:</span>{' '}
              <span className="font-mono text-xs">
                {formatDateTime(requestLog.tenantRequest?.requestDate)}
              </span>
            </div>
            {requestLog.tenantResponse?.responseTime && (
              <div
                className={`font-mono font-bold ${getDurationColor(requestLog.tenantResponse.responseTime)}`}
              >
                {formatDuration(requestLog.tenantResponse.responseTime)}
              </div>
            )}
            <svg
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          <div className="bg-base-300/30 rounded-lg p-3">
            <span className="font-semibold text-base-content/70 text-xs">
              Request ID
            </span>
            <div className="font-mono text-xs text-base-content mt-1">
              {requestLog.requestId}
            </div>
          </div>

          {requestLog.tenantRequest?.content && (
            <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-blue-400" />
                <h5 className="font-semibold text-blue-300 text-sm">
                  Tenant Request
                </h5>
              </div>
              <ExpandableJsonPanel
                title=""
                data={requestLog.tenantRequest.content}
                maxHeight="max-h-64"
                preClassName="bg-slate-950/80 border border-blue-500/20 font-mono text-blue-100"
                defaultExpanded={false}
              />
            </div>
          )}

          {requestLog.serviceRequest &&
            requestLog.serviceRequest.length > 0 &&
            requestLog.serviceRequest.map((serviceReq, index) => (
              <div
                key={`service-req-${index}`}
                className="rounded-lg bg-cyan-500/10 border border-cyan-500/30 p-3"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400" />
                  <h5 className="font-semibold text-cyan-300 text-sm">
                    Service Request - {serviceReq.service}
                    <span className="ml-2 text-xs opacity-70">
                      {formatDateTime(serviceReq.requestDate)}
                    </span>
                  </h5>
                </div>
                <ExpandableJsonPanel
                  title=""
                  data={serviceReq.content}
                  maxHeight="max-h-64"
                  preClassName="bg-slate-950/80 border border-cyan-500/20 font-mono text-cyan-100"
                  defaultExpanded={false}
                />
              </div>
            ))}

          {requestLog.serviceResponse &&
            requestLog.serviceResponse.length > 0 &&
            requestLog.serviceResponse.map((serviceRes, index) => (
              <div
                key={`service-res-${index}`}
                className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <h5 className="font-semibold text-emerald-300 text-sm">
                    Service Response - {serviceRes.service}
                    {serviceRes.responseTime && (
                      <span
                        className={`ml-2 ${getDurationColor(serviceRes.responseTime)}`}
                      >
                        ({formatDuration(serviceRes.responseTime)})
                      </span>
                    )}
                  </h5>
                </div>
                <ExpandableJsonPanel
                  title=""
                  data={serviceRes.content}
                  maxHeight="max-h-64"
                  preClassName="bg-slate-950/80 border border-emerald-500/20 font-mono text-emerald-100"
                  defaultExpanded={false}
                />
              </div>
            ))}

          {requestLog.tenantResponse?.content && (
            <div className="rounded-lg bg-green-500/10 border border-green-500/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-green-400" />
                <h5 className="font-semibold text-green-300 text-sm">
                  Tenant Response
                  {requestLog.tenantResponse.responseTime && (
                    <span
                      className={`ml-2 ${getDurationColor(requestLog.tenantResponse.responseTime)}`}
                    >
                      ({formatDuration(requestLog.tenantResponse.responseTime)})
                    </span>
                  )}
                </h5>
              </div>
              <ExpandableJsonPanel
                title=""
                data={requestLog.tenantResponse.content}
                maxHeight="max-h-64"
                preClassName="bg-slate-950/80 border border-green-500/20 font-mono text-green-100"
                defaultExpanded={false}
              />
            </div>
          )}

          {requestLog.tenantCallback &&
            requestLog.tenantCallback.length > 0 && (
              <div className="rounded-lg bg-orange-500/10 border border-orange-500/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-orange-400" />
                  <h5 className="font-semibold text-orange-300 text-sm">
                    Tenant Callbacks
                  </h5>
                </div>
                <div className="text-xs text-orange-300/70">
                  {requestLog.tenantCallback.length} callback(s) - see timeline
                  for details
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  )
}

const TenantCallbackRow = ({
  callback,
  requestLog,
}: {
  callback: RequestLog['tenantCallback'][0]
  requestLog: RequestLog
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const tagColor = 'bg-orange-500/20 text-orange-300'

  // Check if this is a track callback and extract current status
  const isTrackCallback = requestLog.type === 'track'
  const currentStatus = isTrackCallback
    ? (callback.content as any)?.currentStatus?.status
    : null

  return (
    <div className="rounded-box bg-neutral border border-base-300">
      <div
        className="p-4 cursor-pointer hover:bg-base-300/20 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div
              className={`px-2 py-1 rounded text-xs font-semibold ${tagColor} flex items-center gap-1.5`}
            >
              <DocumentTextIcon className="w-3 h-3" />
              TENANT CALLBACK
            </div>
            <span className="font-semibold">
              {requestLog.type?.toUpperCase()}
            </span>
            {currentStatus && (
              <span className="badge badge-info text-xs">{currentStatus}</span>
            )}
            <span className="badge badge-outline text-xs">
              Status: {callback.responseStatusCode}
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-base-content/70">
              <span className="font-medium">Carrier:</span>{' '}
              <span className="font-mono">
                {requestLog.carrierCode?.join(', ') || 'N/A'}
              </span>
            </div>
            <div className="text-base-content/70">
              <span className="font-medium">Time:</span>{' '}
              <span className="font-mono text-xs">
                {formatDateTime(callback.callbackDate)}
              </span>
            </div>
            <svg
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          <div className="bg-base-300/30 rounded-lg p-3">
            <span className="font-semibold text-base-content/70 text-xs">
              Request ID
            </span>
            <div className="font-mono text-xs text-base-content mt-1">
              {requestLog.requestId}
            </div>
          </div>

          <div className="bg-base-300/30 rounded-lg p-3">
            <span className="font-semibold text-base-content/70 text-xs">
              Webhook Details
            </span>
            <div className="mt-2 space-y-1">
              <div className="text-xs">
                <span className="text-base-content/70">URI:</span>{' '}
                <span className="font-mono text-base-content">
                  {callback.webHookURI}
                </span>
              </div>
              <div className="text-xs">
                <span className="text-base-content/70">Auth:</span>{' '}
                <span className="font-mono text-base-content">
                  {callback.auth}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-orange-500/10 border border-orange-500/30 p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-orange-400" />
              <h5 className="font-semibold text-orange-300 text-sm">
                Callback Content
              </h5>
            </div>
            <ExpandableJsonPanel
              title=""
              data={callback.content}
              maxHeight="max-h-64"
              preClassName="bg-slate-950/80 border border-orange-500/20 font-mono text-orange-100"
              defaultExpanded={false}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const ITEMS_PER_PAGE = 20

const ActivityTimeline = ({
  externalId,
  requestLogType,
  emptyMessage,
}: ActivityTimelineProps) => {
  const [currentPage, setCurrentPage] = useState(1)
  const isFetchingAllTraces = useRef(false)
  const isFetchingAllRequestLogs = useRef(false)

  // First fetch request logs to get shipment IDs
  const {
    items: requestLogs,
    isLoading: isLoadingRequestLogs,
    error: requestLogsError,
    hasNextPage: hasNextRequestLogsPage,
    fetchNextPage: fetchNextRequestLogsPage,
  } = useInfiniteAdminResource<RequestLog>(
    'request-logs',
    {
      externalId,
      ...(requestLogType && { type: requestLogType }),
      limit: 50,
    },
    {
      enabled: !!externalId,
    },
  )

  // Automatically fetch all request logs pages
  useEffect(() => {
    if (
      hasNextRequestLogsPage &&
      !isLoadingRequestLogs &&
      !isFetchingAllRequestLogs.current
    ) {
      isFetchingAllRequestLogs.current = true
      fetchNextRequestLogsPage().finally(() => {
        isFetchingAllRequestLogs.current = false
      })
    }
  }, [hasNextRequestLogsPage, isLoadingRequestLogs, fetchNextRequestLogsPage])

  // Collect all shipment IDs from request logs
  const shipmentIds = useMemo(() => {
    const ids = new Set<string>()
    requestLogs.forEach(log => {
      log.serviceResponse?.forEach(response => {
        const shipmentId = response.content?.shipmentId
        if (shipmentId && typeof shipmentId === 'string') {
          ids.add(shipmentId)
        }
      })
    })
    return Array.from(ids)
  }, [requestLogs])

  // Combine externalId with shipment IDs for trace fetching
  const traceTargetIds = useMemo(() => {
    const ids = externalId ? [externalId] : []
    return [...ids, ...shipmentIds]
  }, [externalId, shipmentIds])

  // Fetch traces for all target IDs
  const {
    items: traces,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteAdminResource<Trace>(
    'traces',
    {
      targetId: traceTargetIds.length > 0 ? traceTargetIds : undefined,
      limit: 50,
    },
    {
      enabled: traceTargetIds.length > 0,
    },
  )

  // Automatically fetch all trace pages
  useEffect(() => {
    if (hasNextPage && !isLoading && !isFetchingAllTraces.current) {
      isFetchingAllTraces.current = true
      fetchNextPage().finally(() => {
        isFetchingAllTraces.current = false
      })
    }
  }, [hasNextPage, isLoading, fetchNextPage])

  // Reset to page 1 when externalId changes
  useEffect(() => {
    setCurrentPage(1)
  }, [])

  const timelineItems = useMemo(() => {
    const items: TimelineItem[] = []

    // Add trace transactions
    traces.forEach(trace => {
      trace.transactions?.forEach((transaction: Transaction, i: number) => {
        items.push({
          type: 'trace',
          data: { ...transaction, category: trace.category },
          timestamp: transaction.timeStamp,
          id: `trace-${trace._id || trace.traceId}-${transaction.id}-${i}-${transaction.timeStamp}`,
        })
      })
    })

    // Add request logs
    requestLogs.forEach(requestLog => {
      items.push({
        type: 'requestLog',
        data: requestLog,
        timestamp: requestLog.tenantRequest?.requestDate || '',
        id: requestLog._id,
      })

      // Add tenant callbacks as separate timeline items
      if (requestLog.tenantCallback && requestLog.tenantCallback.length > 0) {
        requestLog.tenantCallback.forEach((callback, index) => {
          items.push({
            type: 'tenantCallback',
            data: {
              callback,
              requestLog,
            },
            timestamp:
              callback.callbackDate ||
              requestLog.tenantRequest?.requestDate ||
              '',
            id: `callback-${requestLog._id}-${index}-${callback.callbackDate || 'no-date'}`,
          })
        })
      }
    })

    // Sort by timestamp (most recent first)
    return items.sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime()
      const dateB = new Date(b.timestamp).getTime()
      return dateB - dateA
    })
  }, [traces, requestLogs])

  // Paginate the timeline items
  const totalPages = Math.ceil(timelineItems.length / ITEMS_PER_PAGE)
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE
    return timelineItems.slice(startIndex, endIndex)
  }, [timelineItems, currentPage])

  // Count items by type
  const itemCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    timelineItems.forEach(item => {
      if (item.type === 'trace' && item.data.layer === 'carrier') {
        const category = item.data.category
        counts[category] = (counts[category] || 0) + 1
      }
    })
    return counts
  }, [timelineItems])

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1))
  }

  if (!externalId) {
    return (
      <div className="rounded-box border border-warning/30 bg-warning/10 p-6 text-sm text-warning">
        No external ID available to fetch traces
      </div>
    )
  }

  const isAnyLoading = isLoading || isLoadingRequestLogs
  const anyError = error || requestLogsError

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">Activity Timeline</h3>
        {timelineItems.length > 0 && (
          <div className="text-sm text-base-content/70">
            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
            {Math.min(currentPage * ITEMS_PER_PAGE, timelineItems.length)} of{' '}
            {timelineItems.length} items
          </div>
        )}
      </div>

      {timelineItems.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          {Object.entries(itemCounts)
            .sort(([, a], [, b]) => b - a)
            .map(([category, count]) => (
              <div key={category} className="badge badge-info badge-outline">
                {count} {category}
              </div>
            ))}
        </div>
      )}

      {isAnyLoading ? (
        <LoadingState />
      ) : anyError ? (
        <ErrorState error={anyError} onRetry={() => {}} />
      ) : timelineItems.length === 0 ? (
        <div className="rounded-box border border-warning/30 bg-warning/10 p-6 text-sm text-warning">
          {emptyMessage || 'No traces or request logs found for this resource.'}
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {paginatedItems.map((item, index) => {
              // Calculate time difference from previous item
              let timeDifference: string | null = null
              if (index > 0) {
                timeDifference = formatTimeDifference(
                  paginatedItems[index - 1].timestamp,
                  item.timestamp,
                )
              }

              return (
                <div key={item.id}>
                  {timeDifference && (
                    <div className="flex items-center justify-center my-2">
                      <div className="text-xs text-base-content/50 bg-base-300/50 px-3 py-1 rounded-full flex gap-1 items-center">
                        <ArrowUpIcon className="w-3 h-3" /> {timeDifference}
                      </div>
                    </div>
                  )}

                  {item.type === 'trace' ? (
                    <TraceRow transaction={item.data} />
                  ) : item.type === 'tenantCallback' ? (
                    <TenantCallbackRow
                      callback={item.data.callback}
                      requestLog={item.data.requestLog}
                    />
                  ) : (
                    <RequestLogComponent requestLog={item.data as RequestLog} />
                  )}
                </div>
              )
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="btn btn-sm btn-outline"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Previous
              </button>
              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="btn btn-sm btn-outline"
              >
                Next
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export { ActivityTimeline }
